'use client';

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  type AudienceSegment,
  type PreferencesState,
  EMPTY_PREFERENCES,
  loadPreferencesFromCookie,
  savePreferencesToCookie,
} from '@/lib/prefs-storage';

export type { AudienceSegment, PreferencesState };

interface PreferencesContextType {
  segment: AudienceSegment | null;
  interests: string[];
  setSegment: (segment: AudienceSegment | null) => void;
  setInterests: (interests: string[]) => void;
  toggleInterest: (slug: string) => void;
  setPreferences: (prefs: PreferencesState) => void;
  clearPreferences: () => void;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferencesState] = useState<PreferencesState>(EMPTY_PREFERENCES);
  const [hydrated, setHydrated] = useState(false);

  const hydrate = useCallback(() => {
    setPreferencesState(loadPreferencesFromCookie());
  }, []);

  // Hydrate from the aks_prefs cookie on mount.
  useEffect(() => {
    hydrate();
    setHydrated(true);
  }, [hydrate]);

  // Cookies don't fire a `storage` event, so cross-tab/cross-app reactivity
  // is picked up by re-reading on focus and visibility change instead.
  useEffect(() => {
    const handleRefresh = () => hydrate();
    window.addEventListener('focus', handleRefresh);
    document.addEventListener('visibilitychange', handleRefresh);
    return () => {
      window.removeEventListener('focus', handleRefresh);
      document.removeEventListener('visibilitychange', handleRefresh);
    };
  }, [hydrate]);

  // Persist on change (skip initial hydration)
  useEffect(() => {
    if (hydrated) savePreferencesToCookie(preferences);
  }, [preferences, hydrated]);

  const setSegment = useCallback((segment: AudienceSegment | null) => {
    setPreferencesState((prev) => ({ ...prev, segment }));
  }, []);

  const setInterests = useCallback((interests: string[]) => {
    setPreferencesState((prev) => ({ ...prev, interests }));
  }, []);

  const toggleInterest = useCallback((slug: string) => {
    setPreferencesState((prev) => ({
      ...prev,
      interests: prev.interests.includes(slug)
        ? prev.interests.filter((s) => s !== slug)
        : [...prev.interests, slug],
    }));
  }, []);

  const setPreferences = useCallback((prefs: PreferencesState) => {
    setPreferencesState({ segment: prefs.segment, interests: prefs.interests });
  }, []);

  const clearPreferences = useCallback(() => {
    setPreferencesState(EMPTY_PREFERENCES);
  }, []);

  return (
    <PreferencesContext.Provider
      value={{
        segment: preferences.segment,
        interests: preferences.interests,
        setSegment,
        setInterests,
        toggleInterest,
        setPreferences,
        clearPreferences,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences(): PreferencesContextType {
  const context = useContext(PreferencesContext);
  if (!context) throw new Error('usePreferences must be used within PreferencesProvider');
  return context;
}
